import { makeStyles } from "tss-react/mui";
import { Grid, InputBase, IconButton, ListItem, ListItemAvatar, ListItemText, List, Avatar, Typography, Divider } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useMission } from "../../../providers/MissionProvider";
import { useAuth, makeId } from "@emberly/zenith-client";
import EmptyState from "../core/EmptyState";
import { useCallback, useMemo, useRef, useState } from "react";
import { ArrowUpward as ArrowUpIcon } from "@mui/icons-material";
import moment from "moment/moment";
import { GetUserReference } from "../../../common/orders";
import { TimeView } from "../core/Time";

const useStyles = makeStyles()(
  (theme) => ({
    emptyState: {
      height: "180px"
    },
    input: {
      background: theme.palette.background.grey,
      borderRadius: theme.spacing(1),
      padding: theme.spacing(1.75),
    },
    iconButton: {
      background: theme.palette.primary.main,
      color: theme.palette.common.white,
      "&:hover": {
        opacity: 0.8,
        background: theme.palette.primary.main,
      },
      "&:disabled": {
        background: theme.palette.background.grey,
      }
    },
    list: {
      maxHeight: "min(360px, 65vh)",
      overflowY: "auto",
      overflowX: "hidden"
    },
    content: {
      whiteSpace: "pre-wrap",
      maxWidth: "100%"
    },
    timeSpan: {
      display: "inline",
      marginLeft: theme.spacing(1.5)
    }
  })
);

export default function MissionNotesSection() {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { locked, mission, pushToList } = useMission();
  const { user } = useAuth();


  const scrollbarRef = useRef(null);

  const notes = mission?.missionNotes?.notes;
  const isEmpty = notes?.length === 0;
  const maxLimitReached = notes?.length >= 100;

  const [text, setText] = useState("");

  const onChange = useCallback((ev) => {
    const v = ev.target.value;
    if (v.length > 4096) {
      v = v.substring(0, 4095);
    }
    setText(v);
  }, []);

  const addNote = useCallback(() => {

    const note = {
      noteId: makeId(),
      created: moment().toDate(),
      sender: GetUserReference(user),
      avatar: user.picture,
      content: text
    };

    setText("");
    pushToList("missionNotes.notes", note);

    if (!!scrollbarRef.current) {
      scrollbarRef.current.scrollTop = 0;
    }

  }, [text, user, pushToList]);

  const renderNotes = useMemo(
    () => notes.map((note, key) => <MissionNote note={note} key={key} divider={key !== 0} />).reverse(),
    [notes, notes.length]
  );

  return (
    <Grid item container spacing={2}>

      {isEmpty ? (
        <Grid item xs={12}>
          <EmptyState
            className={classes.emptyState}
            title={t("mission:cards:missionNotes:emptyStateTitle")}
            description={t("mission:cards:missionNotes:emptyStateDescription")}
          />
        </Grid>
      ) : (
        <Grid item xs={12}>
          <List className={classes.list} ref={scrollbarRef}>
            {renderNotes}
          </List>
        </Grid>
      )}

      <Grid item xs={12}>
        <Grid container spacing={1.5} alignItems="center">

          <Grid item xs>
            <InputBase
              className={classes.input}
              disabled={locked || maxLimitReached}
              onChange={onChange}
              value={text}
              placeholder={maxLimitReached ? t("mission:cards:missionNotes:maxLimitReached") : t("mission:cards:missionNotes:inputPlaceholder")}
              fullWidth
              multiline
            />
          </Grid>

          <Grid item>
            <IconButton className={classes.iconButton} disabled={locked || !text || maxLimitReached} onClick={addNote} size="large">
              <ArrowUpIcon fontSize="inherit" />
            </IconButton>
          </Grid>

        </Grid>
      </Grid>
    </Grid >
  );
}


function MissionNote(props) {
  const { classes } = useStyles();
  const { note, divider } = props;

  return (
    <>
      <ListItem alignItems="flex-start" disableGutters>
        <ListItemAvatar>
          <Avatar alt={note.sender?.name} src={note.avatar} />
        </ListItemAvatar>
        <ListItemText
          primary={
            <>
              <Typography
                component="span"
                variant="subtitle1"
              >
                {note.sender?.name || ""}
              </Typography>
              <Typography
                component="span"
                variant="body2"
                color="textSecondary"
                className={classes.timeSpan}
              >
                <TimeView time={note.created} />
              </Typography>
            </>
          }
          secondary={
            <Typography variant="body1" className={classes.content}>
              {note.content || ""}
            </Typography>
          }
        />
      </ListItem>
      {divider ? <Divider /> : null}
    </>
  );
}
